export const rootStyles = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: ({ palette }) => palette.background.primary,
  color: ({ palette }) => palette.text.primary,
  // Render native (unstyled) scrollbars + form controls in their dark variant in dark mode, so
  // scrollbars aren't an invisible black bar on the dark surfaces.
  colorScheme: ({ palette }) => (palette.mode === 'dark' ? 'dark' : 'light'),

  '& code:not([class*="language-"])': {
    color: ({ palette }) => palette.primary.dark,
    backgroundColor: ({ palette }) => palette.background.secondary,
    padding: '2px 3px',
    borderRadius: 2,
    lineHeight: 1.7,
  },
  '& table.simple tbody tr td': {
    borderColor: ({ palette }) => palette.divider,
  },
  '& table.simple thead tr th': {
    borderColor: ({ palette }) => palette.divider,
  },
  '& a:not([role=button])': {
    color: ({ palette }) => palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& [class^="border-"]': {
    borderColor: ({ palette }) => palette.divider,
  },
  '& [class*="border-"]': {
    borderColor: ({ palette }) => palette.divider,
  },
};

export const contentWrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 3,
  overflow: 'hidden',
  flex: '1 1 auto',
};

export const contentStyles = {
  position: 'relative',
  display: 'flex',
  overflow: 'hidden',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%',
  zIndex: 2,
  WebkitOverflowScrolling: 'touch',
};

/**
 * On native mobile, append a trailing spacer to the END of each scrollable region (an empty row at
 * the bottom of the table body, or an :after block on a generic scroller). This lengthens the
 * scroll so the LAST real row can scroll up clear of the floating BottomNav, while the table's card
 * surface stays continuous under the bar — content still runs under the translucent bar mid-scroll,
 * and there's no white cut-off gap. The table-container frame is dropped so the spacer area below
 * the content doesn't render a stray bordered box under the floating bar.
 */
/**
 * GLOBAL top inset — applied on EVERY native screen (gated on `isMobileNav`, NOT `toolbar.display`).
 * On native the webview extends under the status bar / Dynamic Island, so without this the content
 * (and each page's own top toolbar) starts flush under the Island. Inset it by the top safe area so
 * every page's top controls clear the Island and sit below the brand band. 0 in a browser.
 */
export const mobileTopInsetStyles = {
  paddingTop: 'env(safe-area-inset-top, 0px)',
};

/**
 * BOTTOM-nav inset — only on toolbar-bearing pages (where the floating BottomNav renders). Appends a
 * trailing spacer to scrollers (so the last row clears the bar) and reserves bottom space on
 * non-scrolling pages; drops the table-container frame so the spacer area reads clean under the bar.
 */
export const mobileBottomNavStyles = {
  '& table tbody::after, & [class*="overflow-y-auto"]::after': {
    content: '""',
    display: 'block',
    height: 'calc(96px + env(safe-area-inset-bottom, 0px))',
  },
  '& [class*="MuiTableContainer-root"]': {
    borderColor: 'transparent',
  },
  '&:not(:has(table)):not(:has([class*="overflow-y-auto"]))': {
    paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
  },
};

/**
 * Native-only: a fixed band that fills the top safe-area (status bar / Dynamic Island) so the
 * inaccessible notch zone reads as an intentional brand bar rather than dead space.
 * `pointerEvents: none` so it never intercepts a tap; the `mobileContentPaddingStyles.paddingTop`
 * above keeps the real toolbar controls below the inset. Brand `primary.main` (NOT a light surface):
 * the native shell ships WHITE status-bar glyphs (Capacitor `StatusBar` `style:'DARK'` = light
 * content), which are illegible on a white band — a dark branded band is the contrast they need.
 */
export const safeAreaTopBandStyles = {
  position: 'fixed',
  top: 0,
  right: 0,
  left: 0,
  height: 'env(safe-area-inset-top, 0px)',
  backgroundColor: ({ palette }) => palette.primary.main,
  zIndex: ({ zIndex }) => zIndex.appBar,
  pointerEvents: 'none',
};

/**
 * GLOBAL native overlay safe-area handling. MUI Drawers/Dialogs portal to <body>, so they never
 * inherit the layout's top inset — on a notched device their content runs under the Dynamic Island
 * (the loan-editor side drawers, pricing drawer, etc. all hit this). Inset the portaled papers by
 * the safe areas: edge drawers (left/right/top, full-height) by the top; bottom sheets by the
 * bottom. Injected via <GlobalStyles> only when `isMobileNav`; all values are 0 in a browser.
 */
export const mobileOverlaySafeAreaStyles = {
  '.MuiDrawer-paperAnchorLeft, .MuiDrawer-paperAnchorRight, .MuiDrawer-paperAnchorTop': {
    paddingTop: 'env(safe-area-inset-top, 0px)',
  },
  '.MuiDrawer-paperAnchorBottom': {
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
};

/**
 * GLOBAL dark theming for CKEditor (rich-text note composer, email editor, snippets, etc.).
 * CKEditor is third-party and styles itself via its own `--ck-color-*` CSS variables + a hardcoded
 * white `.ck-content` surface — it never reads the MUI palette, so in dark mode its editing area
 * and toolbar render bright white. Re-point its variables (and the content/toolbar/popups) at the
 * dark theme tokens app-wide. No-op in light. Injected via <GlobalStyles>.
 */
export const ckEditorDarkStyles = ({ palette }) =>
  palette.mode !== 'dark'
    ? {}
    : {
        '.ck.ck-editor, .ck.ck-content, .ck.ck-toolbar, .ck.ck-reset, .ck.ck-reset_all': {
          '--ck-color-base-background': palette.background.paper,
          '--ck-color-base-foreground': palette.background.secondary,
          '--ck-color-base-border': palette.border.input,
          '--ck-color-base-text': palette.text.primary,
          '--ck-color-text': palette.text.primary,
          '--ck-color-toolbar-background': palette.background.secondary,
          '--ck-color-toolbar-border': palette.border.default,
          '--ck-color-button-default-hover-background': palette.background.hoverDefault,
          '--ck-color-button-on-background': palette.background.hoverDefault,
          '--ck-color-button-on-hover-background': palette.background.hoverDefault,
          '--ck-color-panel-background': palette.background.paper,
          '--ck-color-panel-border': palette.border.default,
          '--ck-color-input-background': palette.background.secondary,
          '--ck-color-input-border': palette.border.input,
          '--ck-color-labeled-field-label-background': palette.background.paper,
          '--ck-color-input-disabled-text': palette.text.secondary,
          '--ck-color-list-background': palette.background.paper,
          '--ck-color-list-button-hover-background': palette.background.hoverDefault,
          '--ck-color-dropdown-panel-background': palette.background.paper,
        },
        '.ck.ck-content, .ck.ck-editor__main > .ck-content': {
          backgroundColor: palette.background.paper,
          color: palette.text.primary,
        },
        '.ck.ck-toolbar': {
          backgroundColor: palette.background.secondary,
          color: palette.text.primary,
        },
        '.ck.ck-balloon-panel, .ck.ck-dropdown__panel, .ck.ck-list, .ck.ck-toolbar__items > .ck-button:not(.ck-on), .ck.ck-toolbar__items > .ck-dropdown > .ck-button:not(.ck-on), .ck.ck-toolbar__items > .ck-dropdown > .ck-splitbutton > .ck-button:not(.ck-on)':
          {
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
          },
      };
